/**
 * Filter Panel Component
 * Visual interface for creating and managing widget filters
 */

import { useState, useEffect } from 'react';
import {
  X,
  Plus,
  Trash2,
  Filter,
  Calendar,
  ChevronDown,
} from 'lucide-react';
import {
  FilterCondition,
  FilterGroup,
  WidgetFilterConfig,
  FilterableField,
  FilterFieldType,
  getOperatorsForType,
  createEmptyCondition,
  createEmptyFilterGroup,
} from '../../types/filters';
import { detectFieldTypeFromData, getUniqueValues } from '../../utils/filterData';

interface FilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  filterConfig: WidgetFilterConfig;
  onFilterChange: (config: WidgetFilterConfig) => void;
  data: Record<string, unknown>[];
  fields?: FilterableField[];
}

export function FilterPanel({
  isOpen,
  onClose,
  filterConfig,
  onFilterChange,
  data,
  fields: providedFields,
}: FilterPanelProps) {
  const [localConfig, setLocalConfig] = useState<WidgetFilterConfig>(filterConfig);
  const [availableFields, setAvailableFields] = useState<FilterableField[]>([]);

  // Detect available fields from data if not provided
  useEffect(() => {
    if (providedFields && providedFields.length > 0) {
      setAvailableFields(providedFields);
    } else if (data && data.length > 0) {
      const firstRow = data[0];
      const detectedFields: FilterableField[] = Object.keys(firstRow).map((key) => ({
        name: key,
        label: formatFieldLabel(key),
        type: detectFieldTypeFromData(data, key),
        sampleValues: getUniqueValues(data, key, 10),
      }));
      setAvailableFields(detectedFields);
    }
  }, [data, providedFields]);

  // Sync local config with prop
  useEffect(() => {
    setLocalConfig(filterConfig);
  }, [filterConfig]);

  if (!isOpen) return null;

  const handleAddGroup = () => {
    const newGroup = createEmptyFilterGroup();
    // If we have fields, pre-select the first one
    if (availableFields.length > 0) {
      newGroup.conditions[0].field = availableFields[0].name;
      newGroup.conditions[0].fieldType = availableFields[0].type;
    }
    setLocalConfig({
      ...localConfig,
      groups: [...localConfig.groups, newGroup],
      enabled: true,
    });
  };

  const handleRemoveGroup = (groupId: string) => {
    const newGroups = localConfig.groups.filter((g) => g.id !== groupId);
    setLocalConfig({
      ...localConfig,
      groups: newGroups,
      enabled: newGroups.length > 0,
    });
  };

  const handleAddCondition = (groupId: string) => {
    const newCondition = createEmptyCondition();
    if (availableFields.length > 0) {
      newCondition.field = availableFields[0].name;
      newCondition.fieldType = availableFields[0].type;
    }

    setLocalConfig({
      ...localConfig,
      groups: localConfig.groups.map((group) =>
        group.id === groupId
          ? { ...group, conditions: [...group.conditions, newCondition] }
          : group
      ),
    });
  };

  const handleRemoveCondition = (groupId: string, conditionId: string) => {
    setLocalConfig({
      ...localConfig,
      groups: localConfig.groups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              conditions: group.conditions.filter((c) => c.id !== conditionId),
            }
          : group
      ),
    });
  };

  const handleConditionChange = (
    groupId: string,
    conditionId: string,
    updates: Partial<FilterCondition>
  ) => {
    setLocalConfig({
      ...localConfig,
      groups: localConfig.groups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              conditions: group.conditions.map((c) =>
                c.id === conditionId ? { ...c, ...updates } : c
              ),
            }
          : group
      ),
    });
  };

  const handleGroupLogicChange = (groupId: string, logic: 'and' | 'or') => {
    setLocalConfig({
      ...localConfig,
      groups: localConfig.groups.map((group) =>
        group.id === groupId ? { ...group, logic } : group
      ),
    });
  };

  const handleApply = () => {
    // Filter out empty conditions
    const cleanedConfig: WidgetFilterConfig = {
      ...localConfig,
      groups: localConfig.groups
        .map((group) => ({
          ...group,
          conditions: group.conditions.filter(
            (c) => c.field && (c.operator === 'is_empty' || c.operator === 'is_not_empty' ||
                   c.operator === 'is_true' || c.operator === 'is_false' ||
                   c.operator === 'today' || c.operator === 'this_week' ||
                   c.operator === 'this_month' || c.value !== null)
          ),
        }))
        .filter((group) => group.conditions.length > 0),
    };
    cleanedConfig.enabled = cleanedConfig.groups.length > 0;
    onFilterChange(cleanedConfig);
    onClose();
  };

  const handleClear = () => {
    const emptyConfig: WidgetFilterConfig = {
      enabled: false,
      groups: [],
    };
    setLocalConfig(emptyConfig);
    onFilterChange(emptyConfig);
  };

  const handleQuickFilter = (days: number) => {
    // Find a date field
    const dateField = availableFields.find((f) => f.type === 'date');
    if (!dateField) {
      alert('No date field found');
      return;
    }

    const quickCondition: FilterCondition = {
      id: `quick_${Date.now()}`,
      field: dateField.name,
      fieldType: 'date',
      operator: 'last_n_days',
      value: days,
    };

    const quickGroup: FilterGroup = {
      id: `quick_group_${Date.now()}`,
      logic: 'and',
      conditions: [quickCondition],
    };

    const newConfig: WidgetFilterConfig = {
      enabled: true,
      groups: [quickGroup],
    };

    setLocalConfig(newConfig);
    onFilterChange(newConfig);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/20"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[70vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-[#45bdd6]" />
            <h3 className="font-semibold text-gray-900">Filter</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Quick Filters */}
          <div className="mb-4">
            <p className="text-sm text-gray-500 mb-2">Quick filters:</p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleQuickFilter(0)}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full transition-colors"
              >
                <Calendar className="w-3.5 h-3.5" />
                Today
              </button>
              <button
                onClick={() => handleQuickFilter(7)}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full transition-colors"
              >
                <Calendar className="w-3.5 h-3.5" />
                7 Days
              </button>
              <button
                onClick={() => handleQuickFilter(30)}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full transition-colors"
              >
                <Calendar className="w-3.5 h-3.5" />
                30 Days
              </button>
              <button
                onClick={() => handleQuickFilter(90)}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full transition-colors"
              >
                <Calendar className="w-3.5 h-3.5" />
                90 Days
              </button>
            </div>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400 uppercase">or</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Filter Groups */}
          {localConfig.groups.length === 0 ? (
            <div className="text-center py-8">
              <Filter className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 mb-4">No filters defined</p>
              <button
                onClick={handleAddGroup}
                className="inline-flex items-center gap-2 px-4 py-2 text-[#45bdd6] border border-[#45bdd6] rounded-lg hover:bg-[#45bdd6]/5 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Filter
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {localConfig.groups.map((group, groupIndex) => (
                <FilterGroupComponent
                  key={group.id}
                  group={group}
                  groupIndex={groupIndex}
                  availableFields={availableFields}
                  data={data}
                  onRemoveGroup={() => handleRemoveGroup(group.id)}
                  onAddCondition={() => handleAddCondition(group.id)}
                  onRemoveCondition={(conditionId) =>
                    handleRemoveCondition(group.id, conditionId)
                  }
                  onConditionChange={(conditionId, updates) =>
                    handleConditionChange(group.id, conditionId, updates)
                  }
                  onLogicChange={(logic) => handleGroupLogicChange(group.id, logic)}
                />
              ))}

              <button
                onClick={handleAddGroup}
                className="w-full py-2 text-sm text-gray-500 hover:text-[#45bdd6] border border-dashed border-gray-300 hover:border-[#45bdd6] rounded-lg transition-colors"
              >
                + Add filter group (AND)
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleClear}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            Clear all
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-2 text-sm text-white bg-[#45bdd6] hover:bg-[#3aa8bf] rounded-lg transition-colors"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Filter Group Component
interface FilterGroupComponentProps {
  group: FilterGroup;
  groupIndex: number;
  availableFields: FilterableField[];
  data: Record<string, unknown>[];
  onRemoveGroup: () => void;
  onAddCondition: () => void;
  onRemoveCondition: (conditionId: string) => void;
  onConditionChange: (conditionId: string, updates: Partial<FilterCondition>) => void;
  onLogicChange: (logic: 'and' | 'or') => void;
}

function FilterGroupComponent({
  group,
  groupIndex,
  availableFields,
  data,
  onRemoveGroup,
  onAddCondition,
  onRemoveCondition,
  onConditionChange,
  onLogicChange,
}: FilterGroupComponentProps) {
  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
      {/* Group Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-gray-500 uppercase">
          Filter Group {groupIndex + 1}
        </span>
        <button
          onClick={onRemoveGroup}
          className="p-1 text-gray-400 hover:text-red-500 rounded"
          title="Delete group"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Conditions */}
      <div className="space-y-2">
        {group.conditions.map((condition, conditionIndex) => (
          <div key={condition.id}>
            {conditionIndex > 0 && (
              <div className="flex items-center gap-2 my-2">
                <select
                  value={group.logic}
                  onChange={(e) => onLogicChange(e.target.value as 'and' | 'or')}
                  className="text-xs font-medium text-[#45bdd6] bg-[#45bdd6]/10 border-0 rounded px-2 py-1 cursor-pointer"
                >
                  <option value="and">AND</option>
                  <option value="or">OR</option>
                </select>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
            )}
            <FilterConditionRow
              condition={condition}
              availableFields={availableFields}
              data={data}
              onRemove={() => onRemoveCondition(condition.id)}
              onChange={(updates) => onConditionChange(condition.id, updates)}
              canRemove={group.conditions.length > 1}
            />
          </div>
        ))}
      </div>

      {/* Add Condition */}
      <button
        onClick={onAddCondition}
        className="mt-2 w-full py-1.5 text-xs text-gray-500 hover:text-[#45bdd6] transition-colors"
      >
        + Add condition
      </button>
    </div>
  );
}

// Filter Condition Row
interface FilterConditionRowProps {
  condition: FilterCondition;
  availableFields: FilterableField[];
  data: Record<string, unknown>[];
  onRemove: () => void;
  onChange: (updates: Partial<FilterCondition>) => void;
  canRemove: boolean;
}

function FilterConditionRow({
  condition,
  availableFields,
  data,
  onRemove,
  onChange,
  canRemove,
}: FilterConditionRowProps) {
  const selectedField = availableFields.find((f) => f.name === condition.field);
  const fieldType = selectedField?.type ?? 'string';
  const operators = getOperatorsForType(fieldType);
  const selectedOperator = operators.find((op) => op.value === condition.operator);
  const uniqueValues = selectedField
    ? getUniqueValues(data, selectedField.name, 20)
    : [];

  const handleFieldChange = (fieldName: string) => {
    const field = availableFields.find((f) => f.name === fieldName);
    const newType = field?.type ?? 'string';
    const newOperators = getOperatorsForType(newType);
    onChange({
      field: fieldName,
      fieldType: newType,
      operator: newOperators[0].value,
      value: null,
      value2: undefined,
    });
  };

  return (
    <div className="flex items-center gap-2 bg-white p-2 rounded border border-gray-200">
      {/* Field Select */}
      <div className="relative flex-1 min-w-0">
        <select
          value={condition.field}
          onChange={(e) => handleFieldChange(e.target.value)}
          className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 pr-8 appearance-none bg-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#45bdd6] focus:border-[#45bdd6]"
        >
          <option value="">Select field...</option>
          {availableFields.map((field) => (
            <option key={field.name} value={field.name}>
              {field.label}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>

      {/* Operator Select */}
      <div className="relative w-32">
        <select
          value={condition.operator}
          onChange={(e) => onChange({ operator: e.target.value as FilterCondition['operator'] })}
          className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 pr-8 appearance-none bg-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#45bdd6] focus:border-[#45bdd6]"
        >
          {operators.map((op) => (
            <option key={op.value} value={op.value}>
              {op.label}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>

      {/* Value Input */}
      {selectedOperator?.requiresValue && (
        <div className="flex-1 min-w-0">
          {selectedOperator.valueType === 'date' ? (
            <input
              type="date"
              value={condition.value ? String(condition.value) : ''}
              onChange={(e) => onChange({ value: e.target.value })}
              className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#45bdd6] focus:border-[#45bdd6]"
            />
          ) : selectedOperator.valueType === 'number' ? (
            <input
              type="number"
              value={condition.value !== null ? String(condition.value) : ''}
              onChange={(e) => onChange({ value: e.target.value ? Number(e.target.value) : null })}
              placeholder="Value"
              className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#45bdd6] focus:border-[#45bdd6]"
            />
          ) : uniqueValues.length > 0 && uniqueValues.length <= 20 ? (
            <div className="relative">
              <select
                value={condition.value !== null ? String(condition.value) : ''}
                onChange={(e) => onChange({ value: e.target.value })}
                className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 pr-8 appearance-none bg-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#45bdd6] focus:border-[#45bdd6]"
              >
                <option value="">Select value...</option>
                {uniqueValues.map((val) => (
                  <option key={val} value={val}>
                    {val}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          ) : (
            <input
              type="text"
              value={condition.value !== null ? String(condition.value) : ''}
              onChange={(e) => onChange({ value: e.target.value })}
              placeholder="Enter value..."
              className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#45bdd6] focus:border-[#45bdd6]"
            />
          )}
        </div>
      )}

      {/* Second Value for 'between' */}
      {selectedOperator?.requiresSecondValue && (
        <>
          <span className="text-gray-400 text-sm">-</span>
          <div className="flex-1 min-w-0">
            {selectedOperator.valueType === 'date' ? (
              <input
                type="date"
                value={condition.value2 ? String(condition.value2) : ''}
                onChange={(e) => onChange({ value2: e.target.value })}
                className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#45bdd6] focus:border-[#45bdd6]"
              />
            ) : (
              <input
                type="number"
                value={condition.value2 !== null && condition.value2 !== undefined ? String(condition.value2) : ''}
                onChange={(e) => onChange({ value2: e.target.value ? Number(e.target.value) : undefined })}
                placeholder="To"
                className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#45bdd6] focus:border-[#45bdd6]"
              />
            )}
          </div>
        </>
      )}

      {/* Remove Button */}
      {canRemove && (
        <button
          onClick={onRemove}
          className="p-1 text-gray-400 hover:text-red-500 rounded"
          title="Remove condition"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

// Helper to format field names as labels
function formatFieldLabel(fieldName: string): string {
  return fieldName
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}
